/**
 * Downloads a version of the open document (DESIGN UD-27, UD-30): the Export menu's latest version and original, and the
 * version bar's Download. Markdown is made from the uploaded file (`markdownOf`), so its structure survives; the other formats
 * are the version's text.
 */
import type { ViewModel } from '../../shared/viewmodel.ts';
import { addNotice } from '../state.ts';
import { LATEST } from '../strings-bench.ts';
import { markdownOf } from './chain-control.ts';
import {
	type Appendix,
	type ExportOf,
	type RewriteExportFormat,
	buildRewriteExport,
} from './rewrite-export.ts';

/** Dropped spans named in the notice; the rest are counted. */
const MAX_LISTED = 8;

/** Hands `blob` to the browser as a download named `filename`. */
export function save(blob: Blob, filename: string): void {
	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = filename;
	document.body.append(a);
	a.click();
	a.remove();
	setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Exports `text` (a version of the open document, named by `label` in the notice) in `format`, and downloads it. */
export async function downloadVersion(
	meta: ViewModel['meta'],
	text: string,
	format: RewriteExportFormat,
	of: ExportOf,
	label: string,
	appendix?: Appendix
): Promise<void> {
	let body = text;
	let made: Awaited<ReturnType<typeof markdownOf>> | null = null;
	if (format === 'md') {
		made = await markdownOf(text);
		body = made.markdown;
	}
	const { blob, filename } = await buildRewriteExport(meta, body, format, appendix, of);
	save(blob, filename);
	if (made && of !== 'original') {
		addNotice(LATEST.markdownDone(label, String(made.changed), made.fromSource));
		if (made.moved > 0) addNotice(`${LATEST.markupMoved(String(made.moved))} ${LATEST.reviewTip}`);
		// only what could not be carried is listed (a few, then a count)
		const shown = made.dropped.slice(0, MAX_LISTED);
		if (shown.length > 0)
			addNotice(
				`${LATEST.markupDropped(
					shown.map((d) => LATEST.markupItem(String(d.para), d.text)).join('; '),
					String(made.dropped.length - shown.length)
				)} ${LATEST.reviewTip}`
			);
	}
}
